import type { MessageDefinition, MessageRule, FieldGenerationRule, CoordinateConfig } from '@k-ucap/shared';
import type { MessageInstance } from '@k-ucap/shared';
import { defaultRandom, SeededRandom, type RandomGenerator } from './SeededRandom.js';
import type { LatLonAlt } from './RandomCoordinateService.js';
import {
  generateRandomCoordinate,
  generateRandomNumber,
  generateRandomEnum,
  generateRandomBoolean,
} from './RandomCoordinateService.js';

export interface GenerationContext {
  sequenceNumber: number;
  timestamp: string;
  defaultCoordinate: CoordinateConfig;
  random: RandomGenerator;
  coordinateCache: LatLonAlt | null;
}

const applyFieldRule = (
  fieldKey: string,
  rule: FieldGenerationRule | undefined,
  context: GenerationContext
): unknown => {
  if (!rule) return undefined;
  const random = context.random;
  switch (rule.type) {
    case 'FIXED':
      return rule.value;
    case 'RANDOM_RANGE':
      return generateRandomNumber(rule.min ?? 0, rule.max ?? 100, random);
    case 'RANDOM_ENUM':
      return rule.enumValues ? generateRandomEnum(rule.enumValues, random) : rule.value;
    case 'RANDOM_COORDINATE': {
      const config = rule.coordinateConfig ?? context.defaultCoordinate;
      if (!context.coordinateCache) {
        context.coordinateCache = generateRandomCoordinate(config, random);
      }
      const coord = context.coordinateCache;
      const key = fieldKey.toLowerCase();
      if (key === 'latitude') return coord.latitude;
      if (key === 'longitude') return coord.longitude;
      if (key === 'altitude' || key === 'altitudem') return coord.altitudeM ?? 0;
      return coord.latitude;
    }
    case 'SEQUENCE':
      return context.sequenceNumber;
    case 'TIMESTAMP':
      return context.timestamp;
    case 'EXPRESSION':
      return rule.value;
    default:
      return rule.value;
  }
};

export const generateMessagePayload = (
  rule: MessageRule,
  definition: MessageDefinition,
  context: GenerationContext
): Record<string, unknown> => {
  const payload: Record<string, unknown> = { ...rule.payloadTemplate };
  const random = context.random;

  definition.fields.forEach((field) => {
    const fieldRule = rule.fieldRules[field.key];
    if (fieldRule) {
      const generated = applyFieldRule(field.key, fieldRule, context);
      if (generated !== undefined) {
        payload[field.key] = generated;
      }
      return;
    }

    if (rule.randomization) {
      const key = field.key.toLowerCase();
      if (key === 'latitude' || key === 'longitude' || key === 'altitude' || key === 'altitudem') {
        if (!context.coordinateCache) {
          context.coordinateCache = generateRandomCoordinate(context.defaultCoordinate, random);
        }
        const coord = context.coordinateCache;
        if (key === 'latitude') payload[field.key] = coord.latitude;
        else if (key === 'longitude') payload[field.key] = coord.longitude;
        else payload[field.key] = coord.altitudeM ?? 0;
        return;
      }
      if (key === 'heading' || key === 'headingdeg' || key === 'yaw' || key === 'yawdeg') {
        payload[field.key] = generateRandomNumber(0, 360, random);
        return;
      }
      if (key === 'speed' || key === 'speedmps') {
        payload[field.key] = generateRandomNumber(0, 100, random);
        return;
      }
    }

    if (payload[field.key] === undefined) {
      if (field.dataType === 'boolean') {
        payload[field.key] = generateRandomBoolean(random);
      } else if (field.dataType === 'number' && field.minimum !== undefined && field.maximum !== undefined) {
        payload[field.key] = generateRandomNumber(field.minimum, field.maximum, random);
      } else {
        payload[field.key] = field.demoValue ?? field.defaultValue ?? '';
      }
    }
  });

  return payload;
};

export const generateMessageInstance = (
  rule: MessageRule,
  definition: MessageDefinition,
  context: GenerationContext
): MessageInstance => {
  const payload = generateMessagePayload(rule, definition, context);
  const random = context.random;
  // Deterministic ID when seeded; otherwise use timestamp for uniqueness
  const idSuffix = random === defaultRandom ? `-${Date.now()}` : `-${random.next()}`;
  return {
    id: `${rule.messageType}-${context.sequenceNumber}${idSuffix}`,
    messageType: rule.messageType,
    standard: definition.standard,
    name: definition.name,
    source: rule.sourceRole || definition.sourceRole,
    target: rule.targetRole || definition.targetRole,
    direction: 'OUTBOUND',
    timestamp: context.timestamp,
    fields: payload,
    sequenceNumber: context.sequenceNumber,
  };
};

export const createGenerationContext = (
  sequenceNumber: number,
  defaultCoordinate: CoordinateConfig,
  random: RandomGenerator
): GenerationContext => ({
  sequenceNumber,
  timestamp: random instanceof SeededRandom ? new Date(sequenceNumber * 1000).toISOString() : new Date().toISOString(),
  defaultCoordinate,
  random,
  coordinateCache: null,
});

export const MessageGenerationService = {
  generateMessagePayload,
  generateMessageInstance,
};

export default MessageGenerationService;
